/**
 * Workflow Result Display Component
 *
 * Displays workflow execution results with HTML content from WfResultConfig
 * Supports different result types: PERSONA, COLOR, SCORE, etc.
 */

'use client';

import React, { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import WestIcon from '@mui/icons-material/West';
import { useTranslation } from 'react-i18next';

export type WorkflowResultProps = {
	executionResult: {
		resultCode: string | null;
		resultType: string | null;
		resultLabel: string | null;
		resultLabelEn: string | null;
		resultColor: string | null;
		resultIcon: string | null;
		resultDescription: string | null;
		totalScore: number;
		finalTags: string[];
		computedData?: any;
	};
	resultConfig: {
		label: string;
		labelEn: string | null;
		colorCode: string;
		icon: string | null;
		description: string | null;
		htmlContent: string | null;
		htmlContentEn: string | null;
		level: number;
	} | null;
	locale?: 'th' | 'en';
	categoryName?: string;
	categoryIndex?: number;
	onBack?: () => void;
	onNext?: () => void;
	onShare?: () => void;
};

const WorkflowResultDisplay = (props: WorkflowResultProps) => {
	const {
		executionResult,
		resultConfig,
		locale = 'th',
		categoryName,
		categoryIndex,
		onBack,
		onNext,
		onShare,
	} = props;

	const router = useRouter();
    const {t} = useTranslation();

	const handleTitleClick = () => {
		if (onBack) {
			try {
				onBack();
			} catch (e) {
				/* ignore */
			}
		} else {
			// Fallback to survey homepage
			router.push('/');
		}
	};

	// Choose HTML content based on locale
	const htmlContent =
		locale === 'en'
			? resultConfig?.htmlContentEn || resultConfig?.htmlContent
			: resultConfig?.htmlContent || resultConfig?.htmlContentEn;

	// Sanitize HTML to prevent XSS
	// Simple sanitization: only allow safe HTML tags
	const sanitizedHTML = useMemo(() => {
		if (!htmlContent) return null;

		// For now, trust the HTML from database (admin-controlled content)
		// In production, consider using a proper sanitization library
		// or validate HTML at the time of saving to database
		return htmlContent;
	}, [htmlContent]);

	const label =
		locale === 'en' ? resultConfig?.labelEn || resultConfig?.label : resultConfig?.label || resultConfig?.labelEn;

	const description = resultConfig?.description;

	// If no config or HTML content, show fallback UI
	if (!resultConfig || !sanitizedHTML) {
		return (
			<div className="w-full bg-white backdrop-blur-sm rounded-lg p-6 shadow-sm">
				{categoryName && categoryIndex !== undefined && (
					<div className="text-center mb-4">
						<div
							className="text-[#006F64] font-medium cursor-pointer hover:opacity-70 transition-opacity"
							onClick={handleTitleClick}
							title={`${t('survey.backToCategory')} ${categoryIndex + 1}`}
						>
							{t('survey.atCategory')} {categoryIndex + 1}:{' '}
							<span className="text-lg font-semibold text-[#015C54]">{categoryName}</span>
						</div>
					</div>
				)}

				<div className="flex items-center flex-col gap-4">
					{/* Result Label */}
					{executionResult.resultLabel && (
						<h2 className="text-2xl font-bold text-center">
							{locale === 'en' ? executionResult.resultLabelEn : executionResult.resultLabel}
						</h2>
					)}

					{/* Result Color Badge */}
					{executionResult.resultColor && (
						<div
							className="px-6 py-2 rounded-full text-white font-semibold"
							style={{ backgroundColor: executionResult.resultColor }}
						>
							{executionResult.resultCode}
						</div>
					)}

					{/* Result Description */}
					{executionResult.resultDescription && (
						<div
							className="text-center text-gray-700 workflow-result-content w-full"
							dangerouslySetInnerHTML={{ __html: executionResult.resultDescription }}
						/>
					)}

					{/* Score Display (if applicable) */}
					{executionResult.resultType === 'SCORE' && (
						<div className="text-center">
							<div className="text-4xl font-bold text-[#2A807B]">{executionResult.totalScore}</div>
							<div className="text-sm text-gray-600">คะแนนรวม</div>
						</div>
					)}

					{/* Action Buttons */}
					<div className="w-full flex items-center justify-center gap-4 mt-4">
						{onBack && (
							<button
								id="footer-back-button"
								type="button"
								onClick={() => {
									onBack();
								}}
								aria-label="ย้อนกลับ"
								className="w-fit flex flex-row items-center justify-center gap-2"
							>
								<WestIcon style={{ color: '#2A807B', width: '18px', height: '18px' }} />{' '}
								<p className="text-[16px] text-[#2A807B] font-semibold">{t('survey.previous')}</p>
							</button>
						)}
						{onNext && (
							<button
								id="footer-button"
								onClick={onNext}
							>
								<p>{t('survey.next')}</p>
							</button>
						)}
					</div>
				</div>
			</div>
		);
	}

	// Render with HTML content from WfResultConfig
	return (
		<div className="w-full flex flex-col items-center">
			{categoryName && categoryIndex !== undefined && (
				<div className="text-center mb-4">
					<div
						className="text-[#006F64] font-medium cursor-pointer hover:opacity-70 transition-opacity"
						onClick={handleTitleClick}
						title={`${t('survey.backToCategory')} ${categoryIndex + 1}`}
					>
						{t('survey.atCategory')} {categoryIndex + 1}:{' '}
						<span className="text-lg font-semibold text-[#015C54]">{categoryName}</span>
					</div>
				</div>
			)}

			{/* Render sanitized HTML from database - full width, no card wrapper */}
			<div
				className="workflow-result-content w-full"
				dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
			/>

			{/* Action Buttons - positioned below HTML content */}
			<div className="w-full flex items-center justify-center gap-4 mt-6 mb-8">
				{onShare && (
					<button
						onClick={onShare}
						className="px-6 py-2 bg-[#92C8B7] text-white rounded-full hover:bg-[#7AB5A5] transition-colors"
					>
						แชร์ผลลัพธ์
					</button>
				)}

				{onBack && (
					<button
						id="footer-back-button"
						type="button"
						onClick={() => {
							onBack();
						}}
						aria-label="ย้อนกลับ"
						className="flex flex-row items-center justify-center gap-2"
					>
						<WestIcon style={{ color: '#2A807B', width: '18px', height: '18px' }} />{' '}
						<p className="text-[16px] text-[#2A807B] font-semibold">{t('survey.previous')}</p>
					</button>
				)}

				{onNext && (
					<button
						id="footer-button"
						onClick={onNext}
					>
						<p>{t('survey.next')}</p>
					</button>
				)}
			</div>
		</div>
	);
};

export default WorkflowResultDisplay;
